import { invokeApi } from '@/lib/apiClient';
import { supabase } from '@/integrations/supabase/client';
import { createDeadline } from '@/services/deadlineService';
import type {
  DeadlineCategory,
  DeadlineDayType,
  DeadlinePriority,
} from '@/types/deadlines';

export interface ProposedDeadline {
  title: string;
  description?: string | null;
  dueDate: string;
  dayType?: DeadlineDayType;
  category?: DeadlineCategory;
  priority?: DeadlinePriority;
  isCritical?: boolean;
  legalReference?: string | null;
  confidence?: number;
}

export interface ChronologyEvent {
  id?: string;
  date: string | null;
  eventType: string;
  actor: string | null;
  description: string;
  sourceRef: string | null;
  confidence: number;
  riskTags: string[];
  proposedDeadline: ProposedDeadline | null;
  createdDeadlineId?: string | null;
}

export interface ChronologyAnalysisResult {
  title: string;
  summary: string;
  riskLevel: 'bajo' | 'medio' | 'alto' | 'critico';
  events: ChronologyEvent[];
  warnings: string[];
}

export interface SavedChronology {
  id: string;
  events: ChronologyEvent[];
}

function normalizeConfidence(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0.5;
  return Math.max(0, Math.min(1, value));
}

function normalizeRisk(value: unknown): ChronologyAnalysisResult['riskLevel'] {
  if (value === 'bajo' || value === 'alto' || value === 'critico') return value;
  return 'medio';
}

function normalizeEvent(raw: any): ChronologyEvent {
  return {
    date: raw?.date || raw?.eventDate || null,
    eventType: raw?.eventType || raw?.event_type || 'actuacion',
    actor: raw?.actor || null,
    description: raw?.description || 'Actuacion detectada',
    sourceRef: raw?.sourceRef || raw?.source_ref || null,
    confidence: normalizeConfidence(raw?.confidence),
    riskTags: Array.isArray(raw?.riskTags) ? raw.riskTags : Array.isArray(raw?.risk_tags) ? raw.risk_tags : [],
    proposedDeadline: raw?.proposedDeadline || raw?.proposed_deadline || null,
  };
}

export async function analyzeExpedienteChronology(input: {
  fileName?: string;
  text: string;
  chatId?: string;
}): Promise<ChronologyAnalysisResult> {
  const { data, error } = await invokeApi<ChronologyAnalysisResult>('analyze-expediente-chronology', {
    body: {
      fileName: input.fileName,
      text: input.text,
      chatId: input.chatId,
    },
  });

  if (error || !data) {
    throw error || new Error('No se pudo analizar la cronologia del expediente');
  }

  return {
    title: data.title || input.fileName || 'Cronologia de expediente',
    summary: data.summary || '',
    riskLevel: normalizeRisk(data.riskLevel),
    events: (Array.isArray(data.events) ? data.events : []).map(normalizeEvent),
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
  };
}

export async function saveChronology(input: {
  userId: string;
  analysis: ChronologyAnalysisResult;
  chatId?: string;
  fileName?: string;
  sourceType?: string;
}): Promise<SavedChronology> {
  const { data: chronology, error: chronologyError } = await (supabase as any)
    .from('expediente_chronologies')
    .insert({
      user_id: input.userId,
      chat_id: input.chatId || null,
      title: input.analysis.title,
      file_name: input.fileName || null,
      source_type: input.sourceType || 'chat',
      summary: input.analysis.summary || null,
      risk_level: input.analysis.riskLevel || 'medio',
      status: 'draft',
      metadata: { warnings: input.analysis.warnings || [] },
    })
    .select('id')
    .single();

  if (chronologyError) throw chronologyError;

  const eventsPayload = input.analysis.events.map((event) => ({
    chronology_id: chronology.id,
    user_id: input.userId,
    event_date: event.date || null,
    event_type: event.eventType || 'actuacion',
    actor: event.actor || null,
    description: event.description,
    source_ref: event.sourceRef || null,
    confidence: event.confidence ?? 0.5,
    risk_tags: event.riskTags || [],
    proposed_deadline: event.proposedDeadline || null,
  }));

  const { data: events, error: eventsError } = await (supabase as any)
    .from('expediente_chronology_events')
    .insert(eventsPayload)
    .select('*');

  if (eventsError) throw eventsError;

  return {
    id: chronology.id,
    events: (events || []).map((event: any) => ({
      id: event.id,
      date: event.event_date,
      eventType: event.event_type,
      actor: event.actor,
      description: event.description,
      sourceRef: event.source_ref,
      confidence: normalizeConfidence(event.confidence),
      riskTags: event.risk_tags || [],
      proposedDeadline: event.proposed_deadline || null,
      createdDeadlineId: event.created_deadline_id || null,
    })),
  };
}

export async function createDeadlineFromChronologyEvent(input: {
  userId: string;
  chronologyId: string;
  event: ChronologyEvent;
}) {
  const proposed = input.event.proposedDeadline;
  if (!proposed) throw new Error('El evento no contiene un plazo propuesto');

  const deadline = await createDeadline(
    {
      title: proposed.title || 'Plazo de expediente',
      description: proposed.description || input.event.description,
      due_date: proposed.dueDate,
      original_due_date: proposed.dueDate,
      category: proposed.category || 'otros',
      priority: proposed.priority || (proposed.isCritical ? 'alta' : 'media'),
      day_type: proposed.dayType || 'habiles',
      is_critical: proposed.isCritical || proposed.priority === 'alta',
      legal_reference: proposed.legalReference || undefined,
      source_type: 'chronology',
      source_chronology_id: input.chronologyId,
      source_event_id: input.event.id,
      source_confidence: proposed.confidence ?? input.event.confidence,
    },
    input.userId
  );

  if (deadline && input.event.id) {
    await (supabase as any)
      .from('expediente_chronology_events')
      .update({ created_deadline_id: deadline.id })
      .eq('id', input.event.id);
  }

  return deadline;
}
